import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { rolesApi } from '@/lib/api'
import {
  Shield, Plus, Edit2, Trash2, Lock, Users, Save,
  X, AlertTriangle, Loader2, CheckSquare, Square,
} from 'lucide-react'
import type { Role } from '@/types'

// ── Permission catalogue ─────────────────────────────────────────────────────

const PERM_GROUPS = [
  {
    label: 'Utilizadores',
    perms: [
      { key: 'users.*',    label: 'Gestão completa de utilizadores' },
      { key: 'users.read', label: 'Ver utilizadores' },
      { key: 'users.create', label: 'Criar utilizadores' },
      { key: 'users.update', label: 'Editar utilizadores' },
      { key: 'users.delete', label: 'Eliminar utilizadores' },
    ],
  },
  {
    label: 'Formulários & Submissões',
    perms: [
      { key: 'forms.*',              label: 'Gestão completa de formulários' },
      { key: 'forms.read',           label: 'Ver formulários' },
      { key: 'forms.create',         label: 'Criar formulários' },
      { key: 'form_submissions.*',   label: 'Gestão completa de submissões' },
      { key: 'form_submissions.read',   label: 'Ver submissões' },
      { key: 'form_submissions.create', label: 'Criar submissões (mobile)' },
    ],
  },
  {
    label: 'Workflows & Tarefas',
    perms: [
      { key: 'workflows.*',  label: 'Gestão completa de workflows' },
      { key: 'workflows.read', label: 'Ver workflows' },
      { key: 'tasks.*',      label: 'Gestão completa de tarefas' },
      { key: 'tasks.read',   label: 'Ver tarefas' },
    ],
  },
  {
    label: 'Produção (requer também acesso ao posto)',
    perms: [
      { key: 'production.use', label: 'Registar consumos e produção' },
      { key: 'production.manage', label: 'Gerir catálogo e acessos (não concede exportação ERP)' },
      { key: 'production.export', label: 'Exportar produção para ERP' },
    ],
  },
  {
    label: 'Outros',
    perms: [
      { key: 'devices.*',     label: 'Gestão de dispositivos' },
      { key: 'devices.read',  label: 'Ver dispositivos' },
      { key: 'reports.read',  label: 'Ver relatórios' },
      { key: 'home_cards.*',  label: 'Gestão de cards mobile' },
      { key: 'releases.*',    label: 'Gestão de versões APK' },
    ],
  },
]

// const ALL_PERM_KEYS = PERM_GROUPS.flatMap(g => g.perms.map(p => p.key))

// ── Permission editor ────────────────────────────────────────────────────────

function PermissionEditor({
  value,
  onChange,
}: {
  value: string[]
  onChange: (p: string[]) => void
}) {
  function toggle(key: string) {
    onChange(value.includes(key) ? value.filter(p => p !== key) : [...value, key])
  }

  const hasSuperAdmin = value.includes('*')

  return (
    <div className="space-y-4">
      {/* Super-admin shortcut */}
      <label className={`flex items-center gap-2 rounded-lg border p-3 cursor-pointer transition-colors ${hasSuperAdmin ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}>
        {hasSuperAdmin
          ? <CheckSquare className="h-4 w-4 text-red-600 flex-shrink-0" />
          : <Square className="h-4 w-4 text-gray-400 flex-shrink-0" />}
        <input type="checkbox" className="hidden" checked={hasSuperAdmin} onChange={() => toggle('*')} />
        <div>
          <p className="text-sm font-medium text-gray-900">Acesso total ao sistema (<code>*</code>)</p>
          <p className="text-xs text-gray-500">Concede todas as permissões presentes e futuras</p>
        </div>
      </label>

      {!hasSuperAdmin && PERM_GROUPS.map(group => (
        <div key={group.label}>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{group.label}</p>
          <div className="space-y-1.5">
            {group.perms.map(p => (
              <label key={p.key} className="flex items-center gap-2 cursor-pointer group">
                <button
                  type="button"
                  onClick={() => toggle(p.key)}
                  className={`h-4 w-4 flex-shrink-0 rounded border transition-colors ${value.includes(p.key) ? 'border-primary-600 bg-primary-600' : 'border-gray-300 bg-white group-hover:border-primary-400'}`}
                >
                  {value.includes(p.key) && (
                    <svg viewBox="0 0 12 12" fill="none" className="h-full w-full">
                      <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>
                <span className="text-sm text-gray-700">{p.label}</span>
                <code className="ml-auto text-xs text-gray-400">{p.key}</code>
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Role Form (inline create/edit) ───────────────────────────────────────────

function RoleForm({
  initial,
  onSave,
  onCancel,
  saving,
}: {
  initial?: Partial<Role>
  onSave: (data: { name: string; description: string; permissions: string[] }) => void
  onCancel: () => void
  saving: boolean
}) {
  const [name, setName] = useState(initial?.name ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [permissions, setPermissions] = useState<string[]>(initial?.permissions ?? [])
  const isEdit = Boolean(initial?.id)

  return (
    <div className="rounded-lg border border-primary-200 bg-primary-50 p-5 space-y-4">
      <h3 className="font-semibold text-primary-800">{isEdit ? 'Editar Papel' : 'Novo Papel'}</h3>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Nome do papel *</label>
          <input
            autoFocus
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Ex: QUALITY_INSPECTOR"
            disabled={isEdit && initial?.is_system}
            className="input w-full disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <p className="mt-1 text-xs text-gray-400">Será convertido para MAIÚSCULAS_SEM_ESPAÇOS</p>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Descrição</label>
          <input
            type="text"
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Breve descrição da função deste papel..."
            className="input w-full"
          />
        </div>
      </div>

      <div>
        <label className="mb-2 block text-sm font-medium text-gray-700">Permissões</label>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <PermissionEditor value={permissions} onChange={setPermissions} />
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="btn btn-secondary">
          <X className="mr-2 h-4 w-4" />Cancelar
        </button>
        <button
          onClick={() => onSave({ name, description, permissions })}
          disabled={saving || !name.trim()}
          className="btn btn-primary"
        >
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          Guardar
        </button>
      </div>
    </div>
  )
}

// ── Main Page ────────────────────────────────────────────────────────────────

export default function RolesPage() {
  const qc = useQueryClient()
  const [showCreate, setShowCreate] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [deleteId, setDeleteId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  const { data: roles = [], isLoading } = useQuery({
    queryKey: ['roles'],
    queryFn: () => rolesApi.list().then(r => r.data),
  })

  const createMutation = useMutation({
    mutationFn: (d: { name: string; description: string; permissions: string[] }) =>
      rolesApi.create({ name: d.name, description: d.description || undefined, permissions: d.permissions }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['roles'] }); setShowCreate(false); setError(null) },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Erro ao criar papel'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: { name: string; description: string; permissions: string[] } }) =>
      rolesApi.update(id, { name: data.name, description: data.description || undefined, permissions: data.permissions }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['roles'] }); setEditingId(null); setError(null) },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Erro ao actualizar papel'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => rolesApi.delete(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['roles'] }); setDeleteId(null); setError(null) },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Erro ao eliminar papel'),
  })

  const ROLE_COLORS: Record<string, string> = {
    SUPER_ADMIN: 'bg-red-100 text-red-800 border-red-200',
    TENANT_ADMIN: 'bg-purple-100 text-purple-800 border-purple-200',
    QUALITY_MANAGER: 'bg-blue-100 text-blue-800 border-blue-200',
    PRODUCTION_MANAGER: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    SUPERVISOR: 'bg-cyan-100 text-cyan-800 border-cyan-200',
    OPERATOR: 'bg-green-100 text-green-800 border-green-200',
    AUDITOR: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    VIEWER: 'bg-gray-100 text-gray-700 border-gray-200',
  }

  function permLabel(p: string) {
    const found = PERM_GROUPS.flatMap(g => g.perms).find(x => x.key === p)
    return found?.label ?? p
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Papéis de Utilizadores</h1>
          <p className="mt-1 text-sm text-gray-600">
            Criar e gerir papéis com permissões específicas. Atribui papéis aos utilizadores na página de utilizadores.
          </p>
        </div>
        <button onClick={() => { setShowCreate(true); setEditingId(null) }} className="btn btn-primary">
          <Plus className="mr-2 h-4 w-4" />Novo Papel
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />{error}
          <button onClick={() => setError(null)} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* Create form */}
      {showCreate && (
        <RoleForm
          onSave={d => createMutation.mutate(d)}
          onCancel={() => setShowCreate(false)}
          saving={createMutation.isPending}
        />
      )}

      {/* Roles list */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        </div>
      ) : (
        <div className="space-y-3">
          {roles.map(role => (
            <div key={role.id} className="card p-0 overflow-hidden">
              {editingId === role.id ? (
                <div className="p-5">
                  <RoleForm
                    initial={role}
                    onSave={d => updateMutation.mutate({ id: role.id, data: d })}
                    onCancel={() => setEditingId(null)}
                    saving={updateMutation.isPending}
                  />
                </div>
              ) : (
                <div className="flex items-start gap-4 p-5">
                  {/* Icon */}
                  <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border ${ROLE_COLORS[role.name] ?? 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                    {role.is_system ? <Lock className="h-5 w-5" /> : <Shield className="h-5 w-5" />}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900">{role.name}</span>
                      {role.is_system && (
                        <span className="badge badge-info text-xs">Sistema</span>
                      )}
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <Users className="h-3 w-3" />{role.users_count ?? 0} utilizador(es)
                      </span>
                    </div>
                    {role.description && (
                      <p className="mt-0.5 text-sm text-gray-500">{role.description}</p>
                    )}
                    {/* Permissions */}
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {role.permissions.includes('*') ? (
                        <span className="rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-xs text-red-700 font-medium">
                          * Acesso total
                        </span>
                      ) : role.permissions.length === 0 ? (
                        <span className="text-xs text-gray-400 italic">Sem permissões definidas</span>
                      ) : (
                        role.permissions.map(p => (
                          <span key={p} className="rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs text-gray-600">
                            {permLabel(p)}
                          </span>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-shrink-0 items-center gap-1">
                    <button
                      onClick={() => { setEditingId(role.id); setShowCreate(false) }}
                      className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                      title="Editar"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setDeleteId(role.id)}
                      disabled={role.is_system || (role.users_count ?? 0) > 0}
                      title={
                        role.is_system
                          ? 'Papéis do sistema não podem ser eliminados'
                          : (role.users_count ?? 0) > 0
                            ? `Tem ${role.users_count} utilizador(es) — remova primeiro`
                            : 'Eliminar papel'
                      }
                      className="rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors disabled:cursor-not-allowed disabled:opacity-30"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Confirm delete */}
              {deleteId === role.id && (
                <div className="border-t border-red-100 bg-red-50 px-5 py-3 flex items-center justify-between">
                  <p className="text-sm text-red-700">
                    Tem a certeza que quer eliminar o papel <strong>{role.name}</strong>?
                  </p>
                  <div className="flex gap-2">
                    <button onClick={() => setDeleteId(null)} className="btn btn-secondary px-3 py-1.5 text-xs">Cancelar</button>
                    <button
                      onClick={() => deleteMutation.mutate(role.id)}
                      disabled={deleteMutation.isPending}
                      className="btn btn-danger px-3 py-1.5 text-xs"
                    >
                      {deleteMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Eliminar'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && roles.length === 0 && (
        <div className="py-12 text-center">
          <Shield className="mx-auto h-12 w-12 text-gray-300" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">Nenhum papel definido</h3>
          <p className="mt-2 text-sm text-gray-500">Cria o primeiro papel para controlar o acesso dos utilizadores.</p>
        </div>
      )}
    </div>
  )
}
